import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import type { Bucket, Period } from '@/api/whatsapiStats'

const PERIOD_TO_BUCKET: Record<Period, Bucket> = {
  '1h': '5m',
  '6h': '15m',
  '24h': '1h',
  '7d': '1h',
  '30d': '1d',
}

interface WhatsApiPeriodContextValue {
  period: Period
  bucket: Bucket
  setPeriod: (period: Period) => void
}

const WhatsApiPeriodContext = createContext<WhatsApiPeriodContextValue | null>(null)

interface WhatsApiPeriodProviderProps {
  children: ReactNode
  initialPeriod?: Period
}

export function WhatsApiPeriodProvider({
  children,
  initialPeriod = '24h',
}: WhatsApiPeriodProviderProps) {
  const [period, setPeriod] = useState<Period>(initialPeriod)

  const value = useMemo<WhatsApiPeriodContextValue>(
    () => ({
      period,
      bucket: PERIOD_TO_BUCKET[period],
      setPeriod,
    }),
    [period],
  )

  return (
    <WhatsApiPeriodContext.Provider value={value}>
      {children}
    </WhatsApiPeriodContext.Provider>
  )
}

export function useWhatsApiPeriod(): WhatsApiPeriodContextValue {
  const ctx = useContext(WhatsApiPeriodContext)
  if (!ctx) {
    throw new Error('useWhatsApiPeriod must be used within a WhatsApiPeriodProvider')
  }
  return ctx
}
